// Percorso file: src/pages/home/HomeIncomplete.jsx
//
// LA FINESTRA DI COMPLETAMENTO — la persona che compila la propria scheda
// (RL-7a) e il genitore che compila anche quelle dei figli (RL-7b).
// Era il SEGNAPOSTO di RL-6; da RL-7a è il modulo vero.
//
// È la vista di chi è stato APPROVATO (status ACTIVE) ma ha la scheda
// incompleta: il documento porta `enrolled: false`, cioè il backend ha
// calcolato che mancano dati che il club chiede per la sua figura.
//
// ⚠️ NON è la vista d'attesa. Chi è ancora PENDING vede SEMPRE E SOLO
// HomePending: l'ordine dei controlli che tiene separati i due binari sta nel
// cancello condiviso (RequireRole), non qui.
//
// ⚠️ NESSUN `role` SULLA ROTTA, ed è una decisione, non una dimenticanza
// (rilievo §9.1 della revisione di RL-6). In RequireRole il ramo
// `enrolment === "INCOMPLETE"` RITORNA prima del controllo del ruolo: un
// `role` scritto su quella rotta non verrebbe nemmeno guardato, e sarebbe una
// protezione finta. La finestra è per CHIUNQUE sia ACTIVE con la scheda
// incompleta, qualunque ruolo: è esattamente ciò che serve, e per questo il
// modulo si adatta alla figura di chi lo apre invece di pretenderne una.
//
// ────────────────────────────────────────────────────────────────────────
// PERIMETRO DI RL-7b: LA FAMIGLIA.
//
// RL-7a aveva costruito il modulo dell'ADULTO SU SÉ STESSO. RL-7b tiene quel
// modulo — non lo riscrive — e lo GENERALIZZA a un profilo BERSAGLIO, che può
// essere sé stessi o un figlio già collegato. Sopra ci mette un elenco di
// famiglia: la propria scheda e una scheda per ogni figlio, ognuna col suo
// segno «completo / da completare», ognuna apribile e salvabile per conto suo.
//
// Fuori dal perimetro, e da non aggiungere qui:
//   • CREARE O RIPARARE i legami genitore↔figlio (`guardians`,
//     `linkedProfiles`) — è RL-7d. Qui si lavora sui soli figli GIÀ collegati,
//     e un legame rotto si SEGNALA (riga in errore), non si aggiusta;
//   • le viste amministrative (MemberProfileModal, EditMemberModal) — RL-7c.
//
// ────────────────────────────────────────────────────────────────────────
// CHI DECIDE CHE COSA (e perché questo file decide pochissimo)
//
//   • IL TIMBRO `enrolled` NON SI SCRIVE DA QUI. Non è in nessuna whitelist
//     del backend, e nessun campo di questo modulo lo nomina. Lo RICALCOLA il
//     server dentro il PUT (calcolaEnrolled + ricalcolaEnrolled): il modulo
//     salva i campi, il server ricalcola, la rilettura porta il valore nuovo,
//     e il cancello apre l'app. Vale lo stesso per `status`, `isMinor`,
//     `role`, `dob`, `email` e per tutti i campi amministrativi.
//
//   • LA REGOLA DI FAMIGLIA È DEL SERVER, non di questa pagina (decisione D2).
//     Il PUT su un FIGLIO fa scattare la cascata `ricalcolaEnrolled`
//     (server.js §ENROLLED): il server risale ai `guardians` del figlio e
//     ricalcola il timbro del genitore con `enrolledConFamiglia`. Qui NON si
//     somma nulla: dopo ogni salvataggio si RILEGGE, e se il timbro del
//     genitore si è acceso è il cancello condiviso a portare via da questa
//     pagina. Nessuna `navigate` è scritta in questo file.
//
//   • LE VALIDAZIONI DI QUESTA PAGINA SONO CORTESIA, NON LEGGE — come in
//     `Enrol.jsx`. Servono a non far premere «Save» a vuoto e a dire che cosa
//     manca ancora. L'autorità sul «completo» resta del server, e il modulo
//     non finge mai di saperlo prima di lui: il messaggio dopo il salvataggio
//     riferisce ciò che il documento riletto dice, non ciò che il modulo
//     aveva previsto.
//
//   • L'ELENCO DEI CAMPI OBBLIGATORI È RICALCATO su `calcolaEnrolled`
//     (server.js §ENROLLED). È una COPIA, e come ogni copia può divergere: se
//     diverge, la conseguenza è un asterisco in più o in meno, mai un dato
//     rifiutato o un timbro sbagliato. Non si è potuta evitare — il computo
//     vive sul server e nessuna rotta lo espone — ed è dichiarata qui perché
//     chi cambierà quell'elenco sappia che esiste anche questa.
//
//   • LA FIGURA SI LEGGE COL METRO DEL SERVER, cioè `role` di PRIMO LIVELLO
//     in sola grafia (`normalizeRole`) e `isMinor`, MAI `canonicalRole` e mai
//     i contexts. Il server sceglie l'elenco dei campi con
//     `normalizzaRuolo(doc.role)`, che è solo maiuscole e spazi: per lui un
//     documento storico `role: "Manager"` NON è un COACH e il WWC non glielo
//     chiede. Se qui si usasse il vocabolario dei ruoli, il modulo
//     pretenderebbe da quella persona due campi che non accenderanno mai il
//     suo timbro — un vicolo cieco perfettamente inutile.
//     Vedi `figuraDelDocumento`, che ricalca l'ORDINE del server.
//
// ────────────────────────────────────────────────────────────────────────
// SALVATAGGIO PARZIALE (decisione del capomastro, Q1=(a))
//
// Un bottone «Save» esplicito per ogni scheda, e nient'altro: nessun
// salvataggio automatico, nessun invio al cambio di campo. Si può salvare a
// metà, chiudere e riprendere: al rientro il modulo rilegge i valori dai
// documenti, che sono la sua sola memoria — non esiste nessuna bozza locale.
// Perciò «Save» NON è sbarrato dai campi obbligatori vuoti; è sbarrato solo da
// un campo SCRITTO MALE (un'email d'emergenza senza chiocciola, un telefono
// che non è un numero): quello non è «a metà», è sbagliato.
//
// ────────────────────────────────────────────────────────────────────────
// FILE (foto, documento d'identità, altro documento) — «OPZIONE A»
//
// Salgono a `POST /upload`, con `api.postForm`, esattamente come fa oggi
// EditMemberModal: stesso contratto, stessi tre campi ammessi dal backend
// (`photo`, `idDocument`, `otherDocument`), stesso `userId` nel corpo — che da
// RL-7b può essere l'id di un FIGLIO, perché quella rotta ha il ramo tutore
// (server.js). NON si scrive su Storage direttamente: lo Storage è
// chiuso (opzione A) e un URL composto a mano non si apre più. I file già in
// archivio si mostrano con FETCH AUTENTICATA — `AuthAvatar` per la foto,
// GET /users/:id/id-document e /other-document per i documenti — perché un
// <img src> o un <a href> non possono allegare l'header Authorization.
//
// ⚠️ NESSUN FILE BLOCCA IL TIMBRO, e il modulo lo dice: `calcolaEnrolled`
// lascia FUORI dal computo photoUrl, idDocument, otherDocument e
// wwc.documentUrl. Chi non ha una foto è comunque completo.
//
// ⚠️ ORDINE DEL SALVATAGGIO: PRIMA i dati, POI i file. È l'ordine deciso
// dall'utente il 7/8/2026 per EditMemberModal, e vale la stessa ragione: le
// due scritture non si possono fondere in una, e se deve cadere qualcosa è
// meglio che restino dati nuovi senza file, non file nuovi su un'anagrafica
// vecchia.
//
// ⚠️ RITOCCO R1 — UN FILE OPZIONALE SI PUÒ ABBANDONARE.
// Fino a RL-7a un caricamento fallito (il caso vero: un file oltre i 10 MB di
// multer, server.js) fermava tutto PRIMA della rilettura, teneva il
// file nel modulo e invitava a ripremere Save: chi aveva la scheda già
// completa restava chiuso in questa pagina, e ogni Save ritentava lo stesso
// file troppo grande. Da RL-7b il file opzionale è, appunto, opzionale:
//   1. la misura si controlla PRIMA di spedire (`oltreIlLimite`): un file
//      troppo grande non viene nemmeno allegato, e lo si dice subito, mentre
//      la persona è ferma sul modulo e può sceglierne uno più piccolo;
//   2. se il caricamento fallisce lo stesso (linea caduta, 403, guasto), i
//      dati SONO salvati, lo si dice, i file scelti si ABBANDONANO — così il
//      Save successivo non li ritenta — e si prosegue con la rilettura, che
//      è ciò che apre l'app a chi ha finito.
// Il prezzo dichiarato: se la rilettura accende il timbro, il cancello
// ridisegna e l'avviso sul file se ne va con la pagina. È il verso giusto in
// cui sbagliare — nessuno resta prigioniero di un allegato facoltativo — ed è
// il motivo per cui il controllo (1) esiste: il caso frequente si ferma prima,
// con la persona ancora davanti al messaggio.
// La gestione dell'errore di multer LATO SERVER non si tocca: è in coda a
// parte (U1-S). R1 è solo frontend.
//
// ────────────────────────────────────────────────────────────────────────
// RESA GRAFICA: opzione (a), nessun restyling, nessuna libreria nuova.
// Il telaio è `HomeShell`, come le altre home. I campi usano le classi già
// esistenti del tema (`qpsc-field`, `qpsc-input`, `qpsc-select`,
// `qpsc-check`, `qpsc-tool-btn`, `qpsc-ghost-btn` — src/components/
// AdminTools.css §3-4, attuazione del doc 07 §8), che sono le uniche classi
// di modulo già ricostruite col tema. NON si usano quelle di `Register.css`,
// che vestono la pagina d'iscrizione intera (`.register-container`, fondo e
// larghezza propri) e dentro la colonna di HomeShell si azzufferebbero col
// telaio. Nessuna classe nuova, nessun foglio nuovo, nessun esadecimale
// scritto a mano (doc 07 §10.1). `react-webcam` è già una dipendenza del
// progetto (la usa MemberProfileModal per lo stesso selfie).
//
// ⚠️ Le schede di famiglia NON usano `.qpsc-person`: quel blocco degli
// strumenti admin diventa `display: contents` sopra i 768px (AdminTools.css,
// media query §8.1) e fuori dalla griglia `.qpsc-admin-list` si dissolverebbe.
// La card qui è composta con le variabili del tema in stile in linea, come già
// fa `Sezione`: nessuna classe nuova, nessun colore scritto a mano.

import React, { useEffect, useMemo, useRef, useState } from "react";
import Webcam from "react-webcam";
import HomeShell from "../../components/HomeShell";
import AuthAvatar from "../../components/AuthAvatar";
import { useAuth } from "../../context/AuthContext";
import { api, API_ROOT, authHeaders } from "../../api/api";
import { ROLE_LABELS, normalizeRole } from "../../auth/access";
// Riuso, non copia: sono le funzioni già scritte e collaudate per i moduli
// d'iscrizione (RL-4/RL-5a). `GENERI` è M/F perché M/F è ciò che il server
// accetta; `emailBenFormata` è lo stesso controllo di forma di lì.
import { GENERI, emailBenFormata } from "../../utils/enrolDati";
import { LinkTermini } from "../enrolShared";
// Gli id dei figli da `linkedProfiles`: la regola di lettura (criterio
// tollerante, avvertenze sul legame autorevole) ha sede in utils/famiglia.js.
import { idsDeiFigli } from "../../utils/famiglia";
import "../../components/AdminTools.css";

// ======================================================================
// L'ELENCO DEI CAMPI OBBLIGATORI, PER FIGURA
// ======================================================================
// Ricalcato su `calcolaEnrolled` (server.js): stessi campi, stesso ordine,
// stessa biforcazione. Vedi l'avvertenza in testa al file: è una copia
// dichiarata, e serve solo a mettere gli asterischi e a dire che cosa manca.
//
// `middleName`, `ffaNumber`, i contatti d'emergenza e tutti i file stanno
// FUORI dal computo per decisione esplicita (D11, D12): sono facoltativi e
// il modulo lo scrive a chiare lettere accanto ai campi.
const OBBLIGATORI_ANAGRAFICA = [
  ["firstName", "First name"],
  ["lastName", "Last name"],
  ["gender", "Gender"],
  ["cityOfBirth", "City of birth"],
  ["countryOfBirth", "Country of birth"],
  ["nationality", "Nationality"],
  ["street", "Street address"],
  ["suburb", "Suburb"],
  ["postcode", "Postcode"],
  ["state", "State"],
];

const OBBLIGATORI_CONTATTO = [["phone", "Phone"]];

// ⚠️ IL WWC DEL COACH NON È FRA GLI OBBLIGATORI DEL MODULO (D-14, decisione
// del committente dell'1/10/2026, via (a)). Il server lo chiede per il timbro
// (CAMPI_ENROLLED_COACH: `wwc.number`, `wwc.expiry`), ma `wwc` sta fra i
// CAMPI_AMMINISTRATIVI: il coach non può scriverselo, e il PUT lo scarterebbe
// in silenzio. Lo registra la segreteria. Il modulo perciò non lo pretende,
// non lo manda, e nella sezione del WWC lo dice a chiare lettere (vedi
// `wwcRegistrato` e la sezione «Working with Children Check» più sotto).

// Il genitore risponde su una scheda più corta (niente luogo di nascita,
// niente nazionalità, niente genere, niente disabilità) e in più sui figli.
const OBBLIGATORI_PARENT = [
  ["firstName", "First name"],
  ["lastName", "Last name"],
  ["street", "Street address"],
  ["suburb", "Suburb"],
  ["postcode", "Postcode"],
  ["state", "State"],
  ["phone", "Phone"],
];

// I due campi che il server chiede IN PIÙ al minore (CAMPI_ENROLLED_MINORE).
// ⚠️ Al minore NON si chiedono `email` né `phone`: il computo del server non
// gli applica CAMPI_ENROLLED_CONTATTO (server.js, calcolaEnrolled), e un
// bambino non ha recapiti propri — quelli sono del genitore.
const OBBLIGATORI_MINORE = [
  ["languageAtHome", "Language spoken at home"],
  ["school", "School"],
];

// ⚠️ `dob` ed `email` sono nel computo del server ma NON in questi elenchi, e
// non è una svista: nessuno dei due si scrive da questa rotta (vedi la
// whitelist «sé stessi» del backend, dove `dob` è passato fra gli
// amministrativi ed `email` non è mai entrato). Metterli fra gli obbligatori
// del modulo significherebbe segnare come «da compilare» due caselle che il
// modulo non può compilare. Si mostrano in sola lettura, e se sono vuoti lo
// si dice con un avviso che manda in segreteria: è l'unico rimedio vero.
// Vedi `bloccatiMancanti`, che sa anche A CHI il server li chiede davvero.

// ======================================================================
// I FILE OPZIONALI — LA MISURA (ritocco R1)
// ======================================================================
// Lo stesso limite di multer (server.js). È una COPIA, come quella
// dei campi obbligatori, e come quella è dichiarata: se un giorno il limite
// del server cambiasse, qui si avviserebbe con la misura sbagliata — mai si
// lascerebbe passare un file che il server rifiuta in silenzio, perché il
// rifiuto è comunque trattato (vedi `salva`).
const LIMITE_FILE_MB = 10;
const LIMITE_FILE = LIMITE_FILE_MB * 1024 * 1024;

const oltreIlLimite = (file) => Boolean(file) && file.size > LIMITE_FILE;

// ======================================================================
// LETTURE DEL DOCUMENTO
// ======================================================================

// Testo di un campo del documento, sempre come stringa. Il `postcode` viaggia
// talvolta come NUMERO (il server lo mette in conto in `valorePresente`): un
// <input> a cui si desse un numero passerebbe da controllato a non
// controllato e React lo direbbe in console.
const testo = (valore) =>
  valore === null || valore === undefined ? "" : String(valore);

// LA FIGURA DEL DOCUMENTO, NELL'ORDINE DEL SERVER (calcolaEnrolled):
// prima il genitore, poi il minore, poi il coach, poi tutti gli altri adulti.
// L'ordine non è indifferente: un documento con `role: "PARENT"` e
// `isMinor: true` per il server è un GENITORE, e chiedergli scuola e lingua di
// casa vorrebbe dire pretendere campi che non accenderanno mai il suo timbro.
//
// ⚠️ `normalizeRole` e non `canonicalRole`: sola grafia, nessuna traduzione —
// il metro del server. Vedi l'avvertenza in testa al file.
function figuraDelDocumento(documento) {
  const doc = documento || {};
  const ruolo = normalizeRole(doc.role);

  if (ruolo === "PARENT") return "PARENT";
  if (doc.isMinor === true) return "MINOR";
  if (ruolo === "COACH") return "COACH";
  return "ADULTO";
}

// Il nome della persona, per le intestazioni dell'elenco di famiglia.
function nomeDelDocumento(documento) {
  const doc = documento || {};
  const nome = `${testo(doc.firstName)} ${testo(doc.lastName)}`.trim();
  return nome;
}

// Un contatto d'emergenza, letto con le DUE grafie che convivono
// nell'anagrafica.
//
// ⚠️ DIVERGENZA REALE, e non la si corregge qui (è backend). Il costruttore
// del documento v2 scrive il numero come `phone`; la whitelist del PUT
// ricompone il contatto leggendo `mobile` — è la grafia che usa tutto il
// frontend, EditMemberModal compreso. Un documento appena nato ha quindi
// `emergency.main.phone`, e la prima scrittura da qualunque modulo lo sposta
// in `emergency.main.mobile`. Nessun dato si perde a patto di LEGGERE
// entrambe e di RISCRIVERE `mobile`, che è l'unica chiave che il PUT
// raccoglie: è quel che si fa qui. La segnalazione è in consegna.
const contattoDalDocumento = (contatto) => {
  const c = contatto && typeof contatto === "object" ? contatto : {};
  return {
    name: testo(c.name),
    mobile: testo(c.mobile) || testo(c.phone),
    email: testo(c.email),
  };
};

// La domanda sulla disabilità ha TRE risposte (sì, no, non dichiarato) e il
// documento le porta come true / false / null. Nella tendina diventano tre
// valori di testo, perché un <select> non conosce i booleani.
const disabilitaDalDocumento = (valore) => {
  if (valore === true) return "yes";
  if (valore === false) return "no";
  return "";
};

// Lo stato iniziale del modulo, letto dal documento del BERSAGLIO — sé stessi
// o un figlio.
// ⚠️ Ci si arriva anche AL RIENTRO dopo un salvataggio parziale: il documento
// è la sola memoria del modulo, non esiste nessuna bozza locale.
function moduloDalDocumento(utente) {
  const u = utente || {};
  const disabilita =
    u.disability && typeof u.disability === "object" ? u.disability : {};
  const wwc = u.wwc && typeof u.wwc === "object" ? u.wwc : {};
  const emergenza =
    u.emergency && typeof u.emergency === "object" ? u.emergency : {};

  return {
    firstName: testo(u.firstName),
    middleName: testo(u.middleName),
    lastName: testo(u.lastName),
    gender: testo(u.gender),
    cityOfBirth: testo(u.cityOfBirth),
    countryOfBirth: testo(u.countryOfBirth),
    nationality: testo(u.nationality),
    street: testo(u.street),
    suburb: testo(u.suburb),
    postcode: testo(u.postcode),
    state: testo(u.state),
    phone: testo(u.phone),
    ffaNumber: testo(u.ffaNumber),
    languageAtHome: testo(u.languageAtHome),
    school: testo(u.school),
    disabilityHas: disabilitaDalDocumento(disabilita.has),
    disabilityDetails: testo(disabilita.details),
    wwcNumber: testo(wwc.number),
    wwcExpiry: testo(wwc.expiry),
    emergencyMain: contattoDalDocumento(emergenza.main),
    emergencySecondary: contattoDalDocumento(emergenza.secondary),
  };
}

// ======================================================================
// VALIDAZIONI DI CORTESIA
// ======================================================================

// Forma del telefono: generosa di proposito. In Australia si scrive
// «0412 345 678», «+61 412 345 678», «(08) 9351 0000»: si accetta qualunque
// grafia purché ci siano almeno sei cifre. Non è una verifica del numero —
// quella la fa solo una telefonata — ed è l'unica cosa onesta da controllare.
function telefonoPlausibile(valore) {
  const cifre = String(valore || "").replace(/\D/g, "");
  return cifre.length >= 6;
}

// I campi obbligatori ancora vuoti, come etichette leggibili.
function mancanti(form, figura) {
  const richiesti =
    figura === "PARENT"
      ? OBBLIGATORI_PARENT
      : figura === "MINOR"
      ? OBBLIGATORI_ANAGRAFICA.concat(OBBLIGATORI_MINORE)
      : OBBLIGATORI_ANAGRAFICA.concat(OBBLIGATORI_CONTATTO);

  const vuoti = richiesti
    .filter(([campo]) => String(form[campo] || "").trim() === "")
    .map(([, etichetta]) => etichetta);

  // La domanda sulla disabilità non è un campo di testo: «non dichiarato»
  // non è una risposta, e un «sì» senza dettagli nemmeno. Il genitore non se
  // la vede chiedere, come sul server (`chiedeDisabilita`).
  if (figura !== "PARENT") {
    if (form.disabilityHas !== "yes" && form.disabilityHas !== "no") {
      vuoti.push("Disability (yes or no)");
    } else if (
      form.disabilityHas === "yes" &&
      form.disabilityDetails.trim() === ""
    ) {
      vuoti.push("Disability details");
    }
  }

  return vuoti;
}

// LE CASELLE CHE IL SERVER CHIEDE E QUESTO MODULO NON PUÒ RIEMPIRE.
// `email` non è mai entrata in whitelist; `dob` è passato fra i campi
// amministrativi. Si mostrano in sola lettura e, se vuote, si manda in
// segreteria: è l'unico rimedio vero.
//
// ⚠️ L'ELENCO DIPENDE DALLA FIGURA, e non è un dettaglio: al GENITORE il
// server non chiede `dob` (CAMPI_ENROLLED_PARENT non lo contiene), al MINORE
// non chiede `email` (CAMPI_ENROLLED_CONTATTO non gli si applica). RL-7a
// avvisava tutti allo stesso modo, e a ogni genitore iscritto da
// /enrol-family — che nasce senza data di nascita, perché non gliela si
// chiede — annunciava un guaio inesistente.
function bloccatiMancanti(documento, figura) {
  const doc = documento || {};
  const vuote = [];

  if (figura !== "MINOR" && testo(doc.email).trim() === "") {
    vuote.push("an email address");
  }

  if (figura !== "PARENT" && testo(doc.dob).trim() === "") {
    vuote.push("a date of birth");
  }

  return vuote;
}

// IL WWC DEL COACH È GIÀ REGISTRATO? Numero e scadenza entrambi presenti, col
// metro del server (`valorePresente` su `wwc.number` e `wwc.expiry`). Serve a
// dire al coach la verità: finché manca, il timbro non si accende, e non
// dipende da lui ma dalla segreteria (D-14).
function wwcRegistrato(documento) {
  const wwc =
    documento && documento.wwc && typeof documento.wwc === "object"
      ? documento.wwc
      : {};
  return testo(wwc.number).trim() !== "" && testo(wwc.expiry).trim() !== "";
}

// I campi SCRITTI MALE — gli unici che sbarrano il salvataggio. Un campo
// vuoto non è mai un errore: il salvataggio parziale è previsto.
//
// ⚠️ Il telefono si controlla solo dove il modulo lo MOSTRA. Sulla scheda di
// un minore non c'è (il minore non ha recapiti propri): un numero malformato
// giacente nel suo documento sbarrerebbe il salvataggio di un campo che la
// famiglia non ha modo di correggere da qui.
function erroriDiForma(form, figura) {
  const problemi = [];

  if (
    figura !== "MINOR" &&
    form.phone.trim() !== "" &&
    !telefonoPlausibile(form.phone)
  ) {
    problemi.push("Phone does not look like a phone number.");
  }

  for (const [contatto, etichetta] of [
    [form.emergencyMain, "main"],
    [form.emergencySecondary, "secondary"],
  ]) {
    if (contatto.mobile.trim() !== "" && !telefonoPlausibile(contatto.mobile)) {
      problemi.push(
        `The ${etichetta} emergency contact number does not look like a phone number.`
      );
    }
    if (contatto.email.trim() !== "" && !emailBenFormata(contatto.email)) {
      problemi.push(
        `The ${etichetta} emergency contact email address is not valid.`
      );
    }
  }

  return problemi;
}

// LA SPUNTA DI CORTESIA DELL'ELENCO DI FAMIGLIA, per il solo GENITORE.
//
// ⚠️ ESISTE PER UNA RAGIONE PRECISA, e va capita prima di toccarla. Il timbro
// `enrolled` del genitore NON parla della sua scheda: parla della FAMIGLIA
// intera (enrolledConFamiglia, server.js). Su questa pagina è `false` per
// definizione — se fosse acceso il cancello avrebbe già portato via — quindi
// usarlo per il segno accanto al proprio nome direbbe «da completare» anche a
// chi ha finito la propria parte, cioè non direbbe nulla. Per la riga del
// genitore si usa perciò questa previsione di cortesia; per le righe dei
// FIGLI no: lì `enrolled` è il verdetto esatto su quella persona e si legge
// quello, come vuole l'incarico.
//
// Il verdetto che apre l'app resta comunque e solo del server.
function schedaCompletaPerCortesia(documento, figura) {
  return (
    mancanti(moduloDalDocumento(documento), figura).length === 0 &&
    bloccatiMancanti(documento, figura).length === 0
  );
}

// La lettura del timbro di un documento ALTRUI (i figli), con la convenzione
// dei lettori: «campo assente = completo» (access.js, isEnrolled). È lo stesso
// metro del cancello; un metro diverso direbbe qui una cosa e là un'altra
// sulla stessa scheda.
const timbroAcceso = (documento) => !documento || documento.enrolled !== false;

// ======================================================================
// COMPOSIZIONE DI CIÒ CHE PARTE
// ======================================================================

// ⚠️ ELENCO CHIUSO, ed è la regola più importante di questo file: partono
// SOLO campi della whitelist «sé stessi» del backend (CAMPI_ANAGRAFICI) —
// che è anche, per intero e senza aggiunte, la whitelist del TUTORE sul
// documento del figlio (server.js).
// Quello che non è qui non si manda — non perché il server lo rifiuterebbe
// rumorosamente, ma perché li SCARTA IN SILENZIO, e un modulo che offra di
// modificare ciò che verrà buttato racconta una bugia a chi lo compila.
//
// Non compaiono, e non devono comparire mai: `enrolled`, `status`, `isMinor`,
// `role`, `dob`, `email`, `emailLower`, `contexts`, `team`, `fees`,
// `guardians`, `linkedProfiles`, `season`, `requestedContext`, `photoUrl`,
// `idDocument`, `otherDocument` (questi ultimi tre li scrive /upload, non
// questa rotta).
//
// ⚠️ E NON PARTE CIÒ CHE IL MODULO NON MOSTRA. Il PUT ignora le chiavi
// assenti (server.js), quindi un campo non spedito resta intatto;
// spedirlo vuoto invece lo CANCELLA. Perciò `phone` non parte dalla scheda di
// un minore, e `languageAtHome`/`school`/`middleName` partono solo da lì.
function corpoDelSalvataggio(form, figura) {
  const contatto = (c) => ({
    name: c.name.trim(),
    // `mobile` e non `phone`: vedi `contattoDalDocumento`. È l'unica chiave
    // che la ricomposizione del PUT raccoglie.
    mobile: c.mobile.trim(),
    email: c.email.trim(),
  });

  const corpo = {
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    gender: form.gender,
    cityOfBirth: form.cityOfBirth.trim(),
    countryOfBirth: form.countryOfBirth.trim(),
    nationality: form.nationality.trim(),
    street: form.street.trim(),
    suburb: form.suburb.trim(),
    postcode: form.postcode.trim(),
    state: form.state.trim(),
    ffaNumber: form.ffaNumber.trim(),
    disability: {
      // Il server non tollera grafie: `has` è true, false o null e nient'altro.
      has:
        form.disabilityHas === "yes"
          ? true
          : form.disabilityHas === "no"
          ? false
          : null,
      details: form.disabilityDetails.trim(),
    },
    emergency: {
      main: contatto(form.emergencyMain),
      secondary: contatto(form.emergencySecondary),
    },
  };

  // Il recapito telefonico è dell'adulto. La scheda del minore non lo mostra
  // e non lo manda: mandarlo vuoto cancellerebbe un numero che un
  // amministratore potrebbe avere scritto lì.
  if (figura !== "MINOR") {
    corpo.phone = form.phone.trim();
  }

  // I tre campi che vivono sulla sola scheda del minore.
  if (figura === "MINOR") {
    corpo.middleName = form.middleName.trim();
    corpo.languageAtHome = form.languageAtHome.trim();
    corpo.school = form.school.trim();
  }

  // ⚠️ `wwc` NON parte, per nessuna figura (D-14): è un campo amministrativo,
  // e per chi compila questo modulo il PUT lo scarterebbe in silenzio. Lo
  // registra la segreteria; la sezione del coach lo mostra in sola lettura.

  return corpo;
}

// ======================================================================
// UN CAMPO
// ======================================================================
// Etichetta sopra, campo sotto (doc 07 §8). L'asterisco NON è decorativo:
// dice che senza quel dato il club non considera consegnata la scheda.
//
// `mancante` (UX-COMPLETAMENTO, F3) è il segno di STATO accanto a quello di
// regola: l'obbligatorio ancora vuoto ha l'etichetta rossa e la nota
// «Required». Vale solo su un campo `obbligatorio`. Si colora l'etichetta e
// non il bordo: il foglio d'eredità globale (EvaluationSheetModal.css) vince
// sul bordo degli `<input>`. Informa e non blocca: il Save resta libero.
function Campo({
  etichetta,
  obbligatorio = false,
  mancante = false,
  nota = "",
  children,
}) {
  const segnato = obbligatorio && mancante;

  return (
    <label className="qpsc-field">
      <span
        className="qpsc-field__label"
        style={segnato ? { color: "var(--qpsc-red-text)" } : undefined}
      >
        {etichetta}
        {obbligatorio ? "*" : ""}
      </span>
      {children}
      {segnato ? (
        <span className="qpsc-note" style={{ color: "var(--qpsc-red-text)" }}>
          Required
        </span>
      ) : null}
      {nota ? <span className="qpsc-note">{nota}</span> : null}
    </label>
  );
}

// Intestazione di una sezione del modulo. Usa la scala tipografica del tema
// (§3.1 XL — intestazioni di sezione) senza inventare una classe nuova.
function Sezione({ titolo, children }) {
  return (
    <section
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--qpsc-space-12)",
      }}
    >
      <h2
        style={{
          margin: 0,
          fontSize: "var(--qpsc-fs-xl)",
          fontWeight: 600,
          color: "var(--qpsc-gold)",
        }}
      >
        {titolo}
      </h2>
      {children}
    </section>
  );
}

// Il segno «completo / da completare» di una riga dell'elenco di famiglia.
// Tre stati, perché il terzo esiste davvero: una scheda che non si è potuta
// leggere non è né completa né incompleta, e dire una delle due sarebbe
// inventare.
function Timbro({ stato }) {
  if (stato === "ignoto") {
    return <span className="qpsc-note">—</span>;
  }

  const completo = stato === "completo";

  return (
    <span
      className="qpsc-note"
      style={{
        // Ambra = manca ancora qualcosa (lo stesso ambra dell'attesa negli
        // strumenti admin), verde = consegnato. Nessun rosso: non è un
        // errore, è un modulo da finire.
        color: completo ? "var(--qpsc-green)" : "var(--qpsc-status-pending)",
      }}
    >
      {completo ? "Complete" : "Still to complete"}
    </span>
  );
}

// ======================================================================
// LA SCHEDA DI UNA PERSONA — il modulo di RL-7a, su un BERSAGLIO
// ======================================================================
// È il modulo di RL-7a, generalizzato: non conosce più «l'utente collegato»,
// conosce un DOCUMENTO BERSAGLIO. Per l'adulto che compila sé stesso il
// bersaglio è il proprio documento e nulla è cambiato; per il genitore il
// bersaglio è, di volta in volta, la propria scheda o quella di un figlio.
//
// Props:
//   documento — il documento del bersaglio, com'è ORA sul server
//   figura    — "PARENT" | "MINOR" | "COACH" | "ADULTO" (figuraDelDocumento)
//   genitore  — solo per le schede MINOR: il documento del genitore che sta
//               compilando. Serve a due cose sole, entrambe dichiarate a
//               schermo: mostrare i recapiti di famiglia in sola lettura e
//               copiare l'indirizzo con la spunta (Q3).
//   rileggi   — funzione che RILEGGE il documento dopo il salvataggio e lo
//               restituisce (o `null` se la rilettura non è riuscita). La
//               fornisce chi monta la scheda, perché il modo di rileggere
//               dipende da CHI è il bersaglio: per sé stessi è
//               `refreshUserFromBackend` (che è ciò che apre l'app), per un
//               figlio è una GET sul suo documento più la rilettura del
//               genitore, che la cascata può aver toccato.
//   form      — il modulo, cioè quel che c'è scritto ADESSO nelle caselle.
//   setForm   — il suo aggiornatore, nella stessa forma di quello di
//               `useState`: accetta sia un valore sia una funzione del
//               precedente.
//               ⚠️ Il modulo NON è più stato di questa scheda: vive nel
//               componente padre (vedi `bozze` in HomeIncomplete), perché una
//               scheda chiusa si smonta e con essa sparirebbe tutto il
//               digitato non ancora salvato.
function SchedaProfilo({
  documento,
  figura,
  genitore = null,
  rileggi,
  form,
  setForm,
}) {
  const idBersaglio = documento && documento.id ? String(documento.id) : "";
  const eMinore = figura === "MINOR";

  // I file scelti ADESSO. Restano `File` finché non si preme Save: nessun
  // caricamento parte da solo, nemmeno il selfie.
  const [foto, setFoto] = useState(null);
  const [anteprimaFoto, setAnteprimaFoto] = useState("");
  const [documentoId, setDocumentoId] = useState(null);
  const [altroDocumento, setAltroDocumento] = useState(null);

  // Cambia a ogni salvataggio e rimonta i campi file: senza, il campo
  // continuerebbe a mostrare il nome di un file che il modulo non tiene più
  // (R1: i file scelti si abbandonano), e il prossimo Save non lo manderebbe.
  const [chiaveFile, setChiaveFile] = useState(0);

  const [fotocameraAperta, setFotocameraAperta] = useState(false);
  const webcamRef = useRef(null);

  // La spunta «stesso indirizzo del genitore» (Q3). È un COMANDO, non un
  // dato: non si salva da nessuna parte, e nel documento del figlio non
  // lascia traccia. Vedi `copiaIndirizzoDalGenitore`.
  const [stessoIndirizzo, setStessoIndirizzo] = useState(false);

  // LA SPUNTA DEI TERMINI, riaccettati per la stagione (D-A). Come quella
  // dell'indirizzo qui sopra è un ATTO, non un dato: non si salva da nessuna
  // parte e nel documento non lascia traccia. Ciò che resta scritto è il flag
  // sulla tessera dell'anno di enrollment, che accende `confirm-season` dentro
  // `salva` — e quello, con `cardSeason`, è il fatto «riaccettati per l'anno
  // X» per intero (server.js, confirm-season: i consensi NON si riscrivono,
  // `acceptedTermsAt` è l'istante della PRIMA accettazione e `acceptedBy`
  // porta la tutela legale sul minore).
  //
  // ⚠️ Vive nella scheda e non nelle bozze del padre, al contrario del
  // digitato: chiudere la scheda la azzera, ed è il verso giusto — una
  // riaccettazione non si eredita da un montaggio precedente, si spunta.
  const [terminiAccettati, setTerminiAccettati] = useState(false);

  const [salvataggioInCorso, setSalvataggioInCorso] = useState(false);
  const [errore, setErrore] = useState("");
  const [conferma, setConferma] = useState("");
  // Avviso NON bloccante, separato dall'errore: riguarda i soli file
  // opzionali (R1). Un file non caricato non è un salvataggio fallito.
  const [avvisoFile, setAvvisoFile] = useState("");
  // UX-COMPLETAMENTO (F1): l'esito di un Save che NON ha acceso il timbro.
  // Ambra e non verde: i dati sono salvati, ma la scheda non è finita.
  const [esitoDaFinire, setEsitoDaFinire] = useState("");

  // ⚠️ UX-COMPLETAMENTO: i ritocchi di resa (F1–F4) valgono per il SOLO
  // percorso di famiglia — la scheda del genitore e quelle che compila per
  // altri. La scheda di chi compila sé stesso fuori da quel percorso (coach,
  // adulto) resta com'era: l'incarico ne lascia fuori il ramo.
  const ramoFamiglia = figura === "PARENT" || Boolean(genitore);

  // Il campo è ancora vuoto? Serve al SOLO segno per campo (F3), ed è a parte
  // di proposito: `mancanti` resta com'è, perché la usano anche la spunta di
  // famiglia e il caso WWC del coach.
  const vuoto = (campo) =>
    ramoFamiglia && String(form[campo] || "").trim() === "";

  const campiMancanti = useMemo(() => mancanti(form, figura), [form, figura]);
  const problemiDiForma = useMemo(
    () => erroriDiForma(form, figura),
    [form, figura]
  );

  const caselleBloccate = bloccatiMancanti(documento, figura);

  const aggiorna = (campo) => (e) =>
    setForm((prec) => ({ ...prec, [campo]: e.target.value }));

  const aggiornaContatto = (quale, campo) => (e) => {
    const valore = e.target.value;
    setForm((prec) => ({
      ...prec,
      [quale]: { ...prec[quale], [campo]: valore },
    }));
  };

  // ⚠️ L'anteprima di un file SCELTO è un object URL, e un object URL non
  // scade da solo: resta appeso al documento con la sua copia del file finché
  // non lo si revoca. Chi prova tre foto prima di decidere ne lascerebbe tre.
  // Si tiene perciò l'ultimo emesso e lo si revoca quando viene sostituito e
  // allo smontaggio. L'anteprima dello SCATTO invece è un data URL — non
  // occupa una voce da revocare — e infatti passa di qui con `false`.
  const objectUrlDaRevocare = useRef("");

  const nuovaAnteprima = (valore, daRevocare) => {
    if (objectUrlDaRevocare.current) {
      URL.revokeObjectURL(objectUrlDaRevocare.current);
    }
    objectUrlDaRevocare.current = daRevocare ? valore : "";
    setAnteprimaFoto(valore);
  };

  useEffect(
    () => () => {
      if (objectUrlDaRevocare.current) {
        URL.revokeObjectURL(objectUrlDaRevocare.current);
      }
    },
    []
  );

  // R1 — LA MISURA SI CONTROLLA PRIMA DI SPEDIRE.
  // Un file oltre il limite non viene allegato affatto: lo si dice subito,
  // mentre la persona è ferma sul modulo, e il campo torna vuoto così non
  // resta l'illusione di un allegato scelto. È il caso vero e frequente —
  // la foto scattata col telefono — e fermarlo qui è l'unico modo perché
  // l'avviso venga letto: al Save la pagina può chiudersi.
  const rifiutaSeTroppoGrande = (file, campoFile) => {
    if (!oltreIlLimite(file)) return false;

    setAvvisoFile(
      `“${file.name}” is larger than ${LIMITE_FILE_MB} MB and was not attached. ` +
        `Please choose a smaller file — or carry on without it: files are optional ` +
        `and do not hold up your membership.`
    );

    if (campoFile) campoFile.value = "";
    return true;
  };

  // Scatta il selfie e lo tiene da parte come file, con l'anteprima. NON lo
  // carica: il caricamento avviene al Save, insieme a tutto il resto.
  const scattaSelfie = () => {
    if (!webcamRef.current) return;

    const immagine = webcamRef.current.getScreenshot();
    if (!immagine) {
      setErrore("The camera did not return a picture. Please try again.");
      return;
    }

    // `getScreenshot` restituisce un data URL: si trasforma in File con la
    // stessa lettura già in uso in MemberProfileModal.
    fetch(immagine)
      .then((r) => r.blob())
      .then((blob) => {
        const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });

        // Uno scatto della fotocamera sta ampiamente sotto il limite, ma la
        // misura si controlla lo stesso: la regola è sui FILE, non su come
        // sono arrivati.
        if (rifiutaSeTroppoGrande(file, null)) return;

        setFoto(file);
        // Data URL: niente da revocare (vedi `nuovaAnteprima`), ma l'object
        // URL eventualmente in mostra prima dello scatto sì.
        nuovaAnteprima(immagine, false);
        setFotocameraAperta(false);
        setErrore("");
        setAvvisoFile("");
      })
      .catch(() =>
        setErrore("The picture could not be prepared. Please try again.")
      );
  };

  const scegliFoto = (e) => {
    const file = (e.target.files && e.target.files[0]) || null;

    if (rifiutaSeTroppoGrande(file, e.target)) {
      setFoto(null);
      nuovaAnteprima("", false);
      return;
    }

    setAvvisoFile("");
    setFoto(file);
    nuovaAnteprima(file ? URL.createObjectURL(file) : "", Boolean(file));
  };

  const scegliDocumento = (imposta) => (e) => {
    const file = (e.target.files && e.target.files[0]) || null;

    if (rifiutaSeTroppoGrande(file, e.target)) {
      imposta(null);
      return;
    }

    setAvvisoFile("");
    imposta(file);
  };

  // Q3 — LA SPUNTA «STESSO INDIRIZZO DEL GENITORE».
  // COPIA SEMPLICE UNA TANTUM, decisa così dal capomastro: al clic si copiano
  // i quattro campi dell'indirizzo del genitore COM'È SCRITTO NEL SUO
  // DOCUMENTO, e da lì in poi i campi del figlio sono suoi, indipendenti e
  // modificabili. NON è un legame: se domani il genitore trasloca, l'indirizzo
  // del figlio non lo segue. Se al momento del clic l'indirizzo del genitore è
  // vuoto o a metà, si copia quello che c'è — nessun errore, nessun rifiuto.
  //
  // ⚠️ Si copia dal DOCUMENTO del genitore, non da quello che il genitore ha
  // digitato nella propria scheda senza salvare: «l'indirizzo attuale del
  // genitore» è quello che il club ha. La nota accanto alla spunta lo dice.
  //
  // Togliere la spunta non disfa nulla: la copia è già avvenuta, e cancellare
  // quattro campi già modificati sarebbe una sorpresa, non un ripensamento.
  const copiaIndirizzoDalGenitore = (e) => {
    const attiva = e.target.checked;
    setStessoIndirizzo(attiva);

    if (!attiva) return;

    const g = genitore || {};

    setForm((prec) => ({
      ...prec,
      street: testo(g.street),
      suburb: testo(g.suburb),
      state: testo(g.state),
      postcode: testo(g.postcode),
    }));
  };

  // Scarica un file già in archivio. Gli endpoint vogliono l'header
  // Authorization, che un <a href> non può allegare: si passa da una fetch
  // autenticata e si consegna il blob al browser. È la stessa funzione di
  // MemberProfileModal, con lo stesso trattamento dell'errore.
  // ⚠️ L'id è quello del BERSAGLIO: per un figlio le due rotte dei documenti
  // riservati hanno il ramo tutore (serviDocumentoRiservato, server.js).
  const scarica = async (endpoint, nomeFile) => {
    try {
      const res = await fetch(`${API_ROOT}/users/${idBersaglio}/${endpoint}`, {
        credentials: "include",
        headers: await authHeaders(),
      });

      if (!res.ok) {
        setErrore("The file could not be opened. Please try again.");
        return;
      }

      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = nomeFile || endpoint;
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Il rilascio immediato interromperebbe lo scaricamento in alcuni
      // browser.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
    } catch {
      setErrore("The file could not be opened. Please try again.");
    }
  };

  const salva = async () => {
    setErrore("");
    setConferma("");
    setAvvisoFile("");
    setEsitoDaFinire("");

    if (!idBersaglio) {
      setErrore("This record could not be identified. Please sign in again.");
      return;
    }

    if (problemiDiForma.length > 0) {
      setErrore(problemiDiForma.join(" "));
      return;
    }

    // ⚠️ Guardia GEMELLA di quella del pulsante, che è già sbarrato senza la
    // spunta. Sta qui perché la decisione di che cosa impedisce il
    // salvataggio vive in un posto solo, e non nel solo `disabled` di un
    // bottone.
    if (!terminiAccettati) {
      setErrore("Please accept the Terms & Policies before saving.");
      return;
    }

    setSalvataggioInCorso(true);

    try {
      // 1) I DATI. Solo campi della whitelist «sé stessi» — che è anche quella
      //    del tutore sul documento del figlio: vedi `corpoDelSalvataggio`.
      await api.put(`/users/${idBersaglio}`, corpoDelSalvataggio(form, figura));

      // 2) I FILE, e solo quelli scelti adesso. Un FormData senza file
      //    farebbe una richiesta inutile, e un suo rifiuto (sessione scaduta,
      //    403) racconterebbe un guasto a chi non aveva scelto nulla.
      const scelti = [
        ["photo", foto],
        ["idDocument", documentoId],
        ["otherDocument", altroDocumento],
      ].filter(([, file]) => file instanceof File);

      // ⚠️ Variabile LOCALE, non lo stato: dentro questa funzione lo stato è
      // il valore catturato al momento del render, e un `setAvvisoFile` appena
      // fatto non lo cambierebbe.
      let avviso = "";

      if (scelti.length > 0) {
        const fd = new FormData();
        for (const [campo, file] of scelti) fd.append(campo, file);
        // ⚠️ L'id del BERSAGLIO. Per un figlio è il varco genitore→figlio di
        // POST /upload (ramo tutore, server.js): dice DOVE si
        // scrive, mentre CHI scrive lo dice sempre e solo il token.
        fd.append("userId", idBersaglio);

        try {
          await api.postForm("/upload", fd);
        } catch {
          // R1 — IL FILE OPZIONALE SI ABBANDONA.
          // I dati SONO salvati: dirlo evita che la persona rifaccia da capo
          // una modifica riuscita. Il file scelto NON resta nel modulo — se
          // restasse, ogni Save successivo lo ritenterebbe e fallirebbe
          // uguale, che è la trappola che R1 chiude — e non si torna
          // indietro: si prosegue con la rilettura, perché un allegato
          // facoltativo non deve tenere fermo chi ha finito la scheda.
          avviso =
            scelti.length > 1
              ? `Your details were saved, but the files could not be uploaded. ` +
                `You can carry on without them and attach them another time — ` +
                `files are optional. If a file is larger than ${LIMITE_FILE_MB} MB, ` +
                `please choose a smaller one.`
              : `Your details were saved, but the file could not be uploaded. ` +
                `You can carry on without it and attach it another time — ` +
                `files are optional. If the file is larger than ${LIMITE_FILE_MB} MB, ` +
                `please choose a smaller one.`;

          // L'anteprima locale se ne va con il file: mostrarla come nuovo
          // ritratto direbbe che è stato caricato.
          nuovaAnteprima("", false);
        }

        // In ogni caso — riuscito o fallito — i file scelti si lasciano
        // andare, e i campi file si rimontano vuoti.
        setFoto(null);
        setDocumentoId(null);
        setAltroDocumento(null);
        setChiaveFile((n) => n + 1);
      }

      setAvvisoFile(avviso);

      // 2-bis) LA CONFERMA DELLA STAGIONE (RENEW-COMPLETAMENTO).
      //
      // È l'atto annuale: accende `seasonConfirmed` su tutte le tessere
      // dell'anno di ENROLLMENT del bersaglio. Vale per TUTTI — chi rinnova
      // e chi si iscrive per la prima volta: la tessera del new user nasce
      // con il flag spento esattamente come quella del rinnovante, e senza
      // questa chiamata il cancello terrebbe dentro anche lui.
      //
      // ⚠️ QUI, e non prima: i dati sono salvati (il PUT è tornato) e gli
      // allegati facoltativi sono già stati tentati. Si conferma quello che
      // c'è scritto adesso, non quello che si stava per scrivere.
      //
      // ⚠️ NESSUN CORPO: quale anno si stia confermando lo decide il server
      // dalla cassettina della stagione, non il client.
      //
      // ⚠️ `try` PROPRIO, come per i file, ma per la ragione OPPOSTA. Questa
      // non è un allegato che si possa abbandonare: se cade, la persona resta
      // fuori dall'app. Il `try` non serve a lasciar perdere, serve a non far
      // dire al `catch` esterno «i dati non sono stati salvati», che sarebbe
      // falso e le farebbe rifare tutto da capo. Si dice la verità — salvati,
      // conferma non registrata — e si prosegue con la rilettura: il cancello
      // terrà la persona qui, dove il Save successivo ritenta. La rotta è
      // idempotente, quindi ritentare non costa nulla.
      let confermaMancata = false;

      try {
        await api.post(`/users/${idBersaglio}/confirm-season`, {});
      } catch {
        confermaMancata = true;
      }

      // 3) LA RILETTURA. È il passaggio che apre l'app: per sé stessi
      //    `refreshUserFromBackend` rimette in AuthContext il documento com'è
      //    ORA, `enrolled` compreso; per un figlio si rilegge il suo documento
      //    E il proprio, perché la cascata di famiglia può aver acceso il
      //    timbro del genitore (ricalcolaEnrolled, server.js).
      //    Se il server ha acceso il timbro del genitore, il cancello
      //    condiviso ridisegna e manda all'instradatore (RequireRole:
      //    `isEnrolled(user)` vero sul ramo INCOMPLETE → <Navigate to="/home">),
      //    che porta alla home del ruolo. Non c'è nessuna `navigate` scritta
      //    qui, ed è voluto: la decisione di dove va una persona ha una sede
      //    sola.
      //
      //    ⚠️ Rilettura fallita = si tiene ciò che si ha, e il suo errore NON
      //    esce di qui. Il salvataggio è già avvenuto: lasciar cadere
      //    l'eccezione nel `catch` esterno direbbe «not saved» a chi ha appena
      //    scritto i propri dati, e lo farebbe rifare tutto da capo. Si dice
      //    «Saved» e basta; l'app si aprirà al prossimo giro, perché all'avvio
      //    AuthContext rilegge da sé.
      let aggiornato = null;

      try {
        aggiornato = rileggi ? await rileggi() : null;
      } catch {
        aggiornato = null;
      }

      // La conferma non è passata: i dati sì. Lo si dice in questi termini,
      // e si indica l'unica mossa utile — ripremere Save. Sta PRIMA del
      // messaggio sul timbro perché è la ragione più recente per cui l'app
      // non si apre, ed è quella su cui la persona può agire subito.
      if (confermaMancata) {
        setConferma(
          "Saved. The club's system could not record your confirmation for " +
            "this season: please press Save again."
        );
        return;
      }

      if (aggiornato && aggiornato.enrolled === false) {
        // Il timbro è ancora spento: si resta sul modulo. Il messaggio
        // riferisce il DOCUMENTO riletto, non la previsione del modulo.
        // ⚠️ D-14: al coach a cui manca SOLO il WWC non si dice «manca
        // qualcosa», che lo manderebbe a cercare un campo che non può
        // riempire: gli si dice che il resto lo chiude la segreteria.
        const soloWwcDelClub =
          figura === "COACH" &&
          !wwcRegistrato(aggiornato) &&
          mancanti(moduloDalDocumento(aggiornato), figura).length === 0 &&
          bloccatiMancanti(aggiornato, figura).length === 0;

        // UX-COMPLETAMENTO (F1): nel percorso di famiglia si dice QUALI campi
        // mancano, ricalcolati sul documento riletto, e in ambra. Se la
        // scheda è a posto e manca qualcosa ai figli, lo si dice così.
        if (ramoFamiglia) {
          const ancoraDaFare = mancanti(moduloDalDocumento(aggiornato), figura);

          if (ancoraDaFare.length > 0) {
            setEsitoDaFinire(
              `Saved, but this record still needs: ${ancoraDaFare.join(", ")}.`
            );
            return;
          }

          if (
            figura === "PARENT" &&
            bloccatiMancanti(aggiornato, figura).length === 0
          ) {
            setEsitoDaFinire(
              'Your details are complete. Now open each child\'s record still marked "Still to complete" and press Save.'
            );
            return;
          }
        }

        const testoEsito =
          figura === "PARENT"
            ? "Saved. The club still needs something: check your own details and each child's record."
            : soloWwcDelClub
            ? "Saved. Your details are complete: your record will be finalised once the club office has recorded your Working with Children Check."
            : "Saved. Some of the details the club needs are still missing.";

        // Anche il ripiego, nel percorso di famiglia, è ambra (F1).
        if (ramoFamiglia) {
          setEsitoDaFinire(testoEsito);
        } else {
          setConferma(testoEsito);
        }
        return;
      }

      setConferma("Saved.");
    } catch {
      // Ci si arriva quando falliscono i DATI — e allora nessun file è
      // partito. Il messaggio dice «not saved» perché è vero nei rifiuti che
      // PRECEDONO la scrittura (sessione scaduta, 403, 400), che sono il caso
      // comune; non è una garanzia, e invitare a riprovare resta la mossa
      // giusta anche negli altri.
      setErrore(
        "Your details were not saved. Please check your connection and try again."
      );
    } finally {
      setSalvataggioInCorso(false);
    }
  };

  const etichettaRuolo =
    ROLE_LABELS[normalizeRole(documento.role)] || testo(documento.role) || "—";

  // Le intestazioni cambiano di PERSONA GRAMMATICALE, e il discrimine non è
  // la minore età: è chi sta compilando. La scheda di un figlio parla di lui e
  // non a lui, perché a riempirla è il genitore — ed è per questo che il
  // discrimine è `genitore`, cioè «questa scheda la sta compilando qualcun
  // altro». Un minore che un giorno entrasse con un'utenza propria (oggi i
  // figli nascono senza `firebaseUid`) si vedrebbe dare del tu, come è giusto.
  const perAltri = Boolean(genitore);
  const nome = nomeDelDocumento(documento);
  const titoloAnagrafica = perAltri
    ? `About ${nome || "this child"}`
    : "About you";
  const titoloIndirizzo = perAltri ? "Where they live" : "Where you live";

  // I messaggi dell'esito, resi in testa al modulo e — nel percorso di
  // famiglia — ripetuti sotto il Save (UX-COMPLETAMENTO, F2): su telefono il
  // capo del modulo è fuori schermo quando si preme il bottone. La copia di
  // sotto non porta `role`, perché un lettore di schermo non li senta due volte.
  const esiti = (annuncia) => (
    <>
      {errore && (
        <p
          className="qpsc-body-text"
          role={annuncia ? "alert" : undefined}
          style={{ color: "var(--qpsc-red-text)" }}
        >
          {errore}
        </p>
      )}

      {/* R1: l'avviso sui file sta per conto suo, e non è rosso — non è un
          errore, è un allegato facoltativo che non è salito. */}
      {avvisoFile && (
        <p
          className="qpsc-body-text"
          role={annuncia ? "status" : undefined}
          style={{ color: "var(--qpsc-status-pending)" }}
        >
          {avvisoFile}
        </p>
      )}

      {/* F1: salvato ma non finito — ambra, come l'avviso sui file. */}
      {esitoDaFinire && (
        <p
          className="qpsc-body-text"
          role={annuncia ? "status" : undefined}
          style={{ color: "var(--qpsc-status-pending)" }}
        >
          {esitoDaFinire}
        </p>
      )}

      {conferma && (
        <p className="qpsc-body-text" style={{ color: "var(--qpsc-green)" }}>
          {conferma}
        </p>
      )}
    </>
  );

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        salva();
      }}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--qpsc-space-24)",
      }}
      autoComplete="off"
    >
      {esiti(true)}

      {/* CHE COSA MANCA ANCORA. Sta in alto perché è la sola domanda che chi
          apre questa scheda si sta facendo. È una previsione di cortesia: il
          computo vero è del server.
          UX-COMPLETAMENTO (F4): nel percorso di famiglia è in ambra, in corpo
          di testo, con la mossa da fare; fuori resta la nota grigia. */}
      {campiMancanti.length > 0 &&
        (ramoFamiglia ? (
          <p
            className="qpsc-body-text"
            style={{ color: "var(--qpsc-status-pending)" }}
          >
            Still missing: {campiMancanti.join(", ")}. Fill these in and press
            Save.
          </p>
        ) : (
          <p className="qpsc-note">Still missing: {campiMancanti.join(", ")}.</p>
        ))}

      <Sezione titolo={perAltri ? "Their record" : "Your record"}>
        {/* SOLA LETTURA, e il modulo non finge il contrario: nessuno di
            questi campi è scrivibile dall'interessato né dal genitore
            (whitelist del backend). Si mostrano perché sono ciò che il club
            ha di questa persona, e perché sono l'unico modo di accorgersi di
            un errore.
            ⚠️ Al GENITORE la data di nascita non si mostra affatto: il server
            non gliela chiede (CAMPI_ENROLLED_PARENT) e l'iscrizione di
            famiglia non gliela domanda, quindi una casella vuota e spenta
            sarebbe solo un allarme falso. */}
        {figura !== "PARENT" && (
          <Campo
            etichetta="Date of birth"
            nota="Set when the record was created. To correct it, contact the club."
          >
            <input
              className="qpsc-input"
              value={testo(documento.dob)}
              disabled
            />
          </Campo>
        )}

        <Campo etichetta="Role" nota="Assigned by the club.">
          <input className="qpsc-input" value={etichettaRuolo} disabled />
        </Campo>

        {/* L'email è la chiave del futuro accesso: si mostra, non si tocca.
            Il backend non la accetta in PUT (non è in whitelist), quindi un
            campo modificabile fallirebbe in silenzio.
            Un minore non ne ha una propria e il server non gliela chiede:
            sulla sua scheda la casella non c'è, e al suo posto ci sono i
            recapiti del genitore, più sotto. */}
        {!eMinore && (
          <Campo
            etichetta="Email"
            nota="The address the club writes to. To change it, contact the club."
          >
            <input
              className="qpsc-input"
              value={testo(documento.email)}
              disabled
            />
          </Campo>
        )}

        {caselleBloccate.length > 0 && (
          <p className="qpsc-note" style={{ color: "var(--qpsc-red-text)" }}>
            {perAltri ? "This record is missing " : "Your record is missing "}
            {caselleBloccate.join(" and ")}. These cannot be entered from this
            page: please contact the club, otherwise the record cannot be
            completed.
          </p>
        )}
      </Sezione>

      <Sezione titolo={titoloAnagrafica}>
        <Campo etichetta="First name" obbligatorio mancante={vuoto("firstName")}>
          <input
            className="qpsc-input"
            value={form.firstName}
            onChange={aggiorna("firstName")}
          />
        </Campo>

        {/* Il secondo nome sta sulla sola scheda del minore, dove l'incarico
            lo chiede. Fuori dal computo del server: nessun asterisco. */}
        {eMinore && (
          <Campo etichetta="Middle name">
            <input
              className="qpsc-input"
              value={form.middleName}
              onChange={aggiorna("middleName")}
            />
          </Campo>
        )}

        <Campo etichetta="Last name" obbligatorio mancante={vuoto("lastName")}>
          <input
            className="qpsc-input"
            value={form.lastName}
            onChange={aggiorna("lastName")}
          />
        </Campo>

        {/* Il genere si raccoglie M/F perché M/F è ciò che il server
            accetta (D5): stessa tendina dei moduli d'iscrizione, stessa
            costante condivisa. Per il genitore non è nel computo, quindi
            non porta l'asterisco. */}
        <Campo
          etichetta="Gender"
          obbligatorio={figura !== "PARENT"}
          mancante={vuoto("gender")}
        >
          <select
            className="qpsc-select"
            value={form.gender}
            onChange={aggiorna("gender")}
          >
            <option value="">Select</option>
            {GENERI.map((g) => (
              <option key={g.valore} value={g.valore}>
                {g.etichetta}
              </option>
            ))}
          </select>
        </Campo>

        <Campo
          etichetta="City of birth"
          obbligatorio={figura !== "PARENT"}
          mancante={vuoto("cityOfBirth")}
        >
          <input
            className="qpsc-input"
            value={form.cityOfBirth}
            onChange={aggiorna("cityOfBirth")}
          />
        </Campo>

        <Campo
          etichetta="Country of birth"
          obbligatorio={figura !== "PARENT"}
          mancante={vuoto("countryOfBirth")}
        >
          <input
            className="qpsc-input"
            value={form.countryOfBirth}
            onChange={aggiorna("countryOfBirth")}
          />
        </Campo>

        <Campo
          etichetta="Nationality"
          obbligatorio={figura !== "PARENT"}
          mancante={vuoto("nationality")}
        >
          <input
            className="qpsc-input"
            value={form.nationality}
            onChange={aggiorna("nationality")}
          />
        </Campo>
      </Sezione>

      <Sezione titolo={titoloIndirizzo}>
        {/* Q3 — la spunta esiste sulla SOLA scheda del figlio, ed è una copia
            istantanea, non un legame. Vedi `copiaIndirizzoDalGenitore`. */}
        {perAltri && (
          <>
            <label className="qpsc-check">
              <input
                className="qpsc-check__input"
                type="checkbox"
                checked={stessoIndirizzo}
                onChange={copiaIndirizzoDalGenitore}
              />
              <span className="qpsc-check__box" aria-hidden="true" />
              <span>Same address as yours</span>
            </label>

            <p className="qpsc-note">
              Ticking this copies your address as it is now. The two addresses
              stay separate afterwards: you can change either without changing
              the other.
            </p>
          </>
        )}

        <Campo etichetta="Street address" obbligatorio mancante={vuoto("street")}>
          <input
            className="qpsc-input"
            value={form.street}
            onChange={aggiorna("street")}
          />
        </Campo>

        <Campo etichetta="Suburb" obbligatorio mancante={vuoto("suburb")}>
          <input
            className="qpsc-input"
            value={form.suburb}
            onChange={aggiorna("suburb")}
          />
        </Campo>

        <Campo etichetta="Postcode" obbligatorio mancante={vuoto("postcode")}>
          <input
            className="qpsc-input"
            inputMode="numeric"
            value={form.postcode}
            onChange={aggiorna("postcode")}
          />
        </Campo>

        <Campo etichetta="State" obbligatorio mancante={vuoto("state")}>
          <input
            className="qpsc-input"
            value={form.state}
            onChange={aggiorna("state")}
          />
        </Campo>
      </Sezione>

      {/* SCUOLA E LINGUA DI CASA: i due campi che il server chiede al solo
          minore (CAMPI_ENROLLED_MINORE). */}
      {eMinore && (
        <Sezione titolo="School and language">
          <Campo
            etichetta="Language spoken at home"
            obbligatorio
            mancante={vuoto("languageAtHome")}
          >
            <input
              className="qpsc-input"
              value={form.languageAtHome}
              onChange={aggiorna("languageAtHome")}
            />
          </Campo>

          <Campo etichetta="School" obbligatorio mancante={vuoto("school")}>
            <input
              className="qpsc-input"
              value={form.school}
              onChange={aggiorna("school")}
            />
          </Campo>
        </Sezione>
      )}

      {/* I RECAPITI. L'adulto dà il proprio numero; il minore non ne ha uno
          suo, e al suo posto si mostrano — in sola lettura — quelli del
          genitore che sta compilando, perché è a lui che il club scrive e
          telefona. */}
      {!eMinore && (
        <Sezione titolo="How the club reaches you">
          <Campo etichetta="Phone" obbligatorio mancante={vuoto("phone")}>
            <input
              className="qpsc-input"
              type="tel"
              value={form.phone}
              onChange={aggiorna("phone")}
            />
          </Campo>
        </Sezione>
      )}

      {eMinore && genitore && (
        <Sezione titolo="How the club reaches this child">
          <p className="qpsc-note">
            A junior member has no contact details of their own: the club
            writes and calls the parent or guardian. To change these, open your
            own record.
          </p>

          <Campo etichetta="Parent or guardian">
            <input
              className="qpsc-input"
              value={nomeDelDocumento(genitore) || "—"}
              disabled
            />
          </Campo>

          <Campo etichetta="Phone">
            <input
              className="qpsc-input"
              value={testo(genitore.phone)}
              disabled
            />
          </Campo>

          <Campo etichetta="Email">
            <input
              className="qpsc-input"
              value={testo(genitore.email)}
              disabled
            />
          </Campo>
        </Sezione>
      )}

      {/* La domanda sulla disabilità non si fa al genitore: il computo del
          server non gliela chiede (è la scheda del figlio a portarla). */}
      {figura !== "PARENT" && (
        <Sezione titolo="Health">
          <Campo
            etichetta={
              perAltri
                ? "Does this child have a disability the club should know about?"
                : "Do you have a disability the club should know about?"
            }
            obbligatorio
            mancante={vuoto("disabilityHas")}
            nota="Answer yes or no. If yes, please tell us what support is needed."
          >
            <select
              className="qpsc-select"
              value={form.disabilityHas}
              onChange={aggiorna("disabilityHas")}
            >
              <option value="">Select</option>
              <option value="no">No</option>
              <option value="yes">Yes</option>
            </select>
          </Campo>

          {form.disabilityHas === "yes" && (
            <Campo
              etichetta="Details"
              obbligatorio
              mancante={vuoto("disabilityDetails")}
            >
              <input
                className="qpsc-input"
                value={form.disabilityDetails}
                onChange={aggiorna("disabilityDetails")}
              />
            </Campo>
          )}
        </Sezione>
      )}

      {/* WWC — SOLO PER I COACH, e solo perché il server chiede proprio a
          loro `wwc.number` e `wwc.expiry`. Nessun minore lo vede: la figura
          MINOR non passa mai di qui. Il DOCUMENTO del WWC non è qui: vedi la
          nota sui file più sotto e la consegna.
          ⚠️ D-14: SOLA LETTURA. `wwc` è un campo amministrativo, lo registra
          la segreteria; il PUT di questo modulo lo scarterebbe in silenzio.
          Si mostra ciò che il club ha, e si dice chi lo scrive. */}
      {figura === "COACH" && (
        <Sezione titolo="Working with Children Check">
          <p className="qpsc-note">
            Your Working with Children Check is recorded by the club office —
            you don't need to enter it here.
            {wwcRegistrato(documento)
              ? ""
              : " Until the club office has recorded it, your record cannot be marked complete."}
          </p>

          <Campo etichetta="WWC number">
            <input
              className="qpsc-input"
              value={form.wwcNumber}
              disabled
            />
          </Campo>

          <Campo etichetta="Expiry date">
            <input
              className="qpsc-input"
              type="date"
              value={form.wwcExpiry}
              disabled
            />
          </Campo>
        </Sezione>
      )}

      <Sezione titolo={eMinore ? "Emergency and other details" : "Optional details"}>
        <p className="qpsc-note">
          Nothing in this section holds up your membership: you can leave it
          all empty and come back to it later.
        </p>

        {/* D11: il numero di tesseramento nazionale sta nel profilo ed è
            facoltativo. */}
        <Campo
          etichetta="FFA number"
          nota={
            perAltri
              ? "Football Australia number, if you know it."
              : "Your Football Australia number, if you know it."
          }
        >
          <input
            className="qpsc-input"
            value={form.ffaNumber}
            onChange={aggiorna("ffaNumber")}
          />
        </Campo>

        {/* D12: i contatti d'emergenza sono facoltativi anche per un minore —
            il computo del server non li guarda. Sulla scheda del figlio il
            contatto principale arriva già scritto: alla nascita del documento
            è una COPIA dei dati del genitore (decisione D4), e da qui si può
            correggere. */}
        <p className="qpsc-note">
          {perAltri
            ? "Emergency contact — main (pre-filled with your details when the record was created)"
            : "Emergency contact — main"}
        </p>

        <Campo etichetta="Name">
          <input
            className="qpsc-input"
            value={form.emergencyMain.name}
            onChange={aggiornaContatto("emergencyMain", "name")}
          />
        </Campo>

        <Campo etichetta="Phone">
          <input
            className="qpsc-input"
            type="tel"
            value={form.emergencyMain.mobile}
            onChange={aggiornaContatto("emergencyMain", "mobile")}
          />
        </Campo>

        <Campo etichetta="Email">
          <input
            className="qpsc-input"
            type="email"
            value={form.emergencyMain.email}
            onChange={aggiornaContatto("emergencyMain", "email")}
          />
        </Campo>

        <p className="qpsc-note">Emergency contact — secondary</p>

        <Campo etichetta="Name">
          <input
            className="qpsc-input"
            value={form.emergencySecondary.name}
            onChange={aggiornaContatto("emergencySecondary", "name")}
          />
        </Campo>

        <Campo etichetta="Phone">
          <input
            className="qpsc-input"
            type="tel"
            value={form.emergencySecondary.mobile}
            onChange={aggiornaContatto("emergencySecondary", "mobile")}
          />
        </Campo>

        <Campo etichetta="Email">
          <input
            className="qpsc-input"
            type="email"
            value={form.emergencySecondary.email}
            onChange={aggiornaContatto("emergencySecondary", "email")}
          />
        </Campo>
      </Sezione>

      <Sezione titolo={eMinore ? "Photo and ID document" : "Photo and documents"}>
        <p className="qpsc-note">
          All optional, and none of them holds up membership. Files are
          uploaded when you press Save, and only the club and you can see them.
          Each file must be {LIMITE_FILE_MB} MB or smaller.
        </p>

        {/* LA FOTO. Quella già in archivio arriva dal backend con fetch
            autenticata (AuthAvatar); quella appena scelta o scattata si
            mostra dall'anteprima locale, perché il percorso su Storage non
            cambia mai (users/<id>/profile.jpg) e un'immagine già scaricata
            resterebbe quella vecchia fino al prossimo avvio. */}
        <Campo etichetta="Profile photo">
          <span
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--qpsc-space-12)",
            }}
          >
            {/* ⚠️ Nell'`alt` non compaiono le parole «photo», «picture» e
                «image»: l'analizzatore di accessibilità le vieta, perché lo
                screen reader annuncia già da sé che si tratta di
                un'immagine e ripeterlo è rumore per chi ascolta. */}
            {anteprimaFoto ? (
              <img
                src={anteprimaFoto}
                alt="The new profile avatar"
                style={{
                  width: "64px",
                  height: "64px",
                  objectFit: "cover",
                  borderRadius: "var(--qpsc-radius)",
                }}
              />
            ) : (
              <AuthAvatar
                userId={idBersaglio}
                photoKey={testo(documento.photoUrl)}
                name={`${form.firstName} ${form.lastName}`}
                alt="Profile avatar"
                style={{
                  width: "64px",
                  height: "64px",
                  objectFit: "cover",
                  borderRadius: "var(--qpsc-radius)",
                }}
              />
            )}

            <input
              key={`foto-${chiaveFile}`}
              className="qpsc-input"
              type="file"
              accept=".jpg,.jpeg,.png"
              onChange={scegliFoto}
            />
          </span>
        </Campo>

        {!fotocameraAperta && (
          <button
            type="button"
            className="qpsc-ghost-btn"
            onClick={() => setFotocameraAperta(true)}
          >
            {perAltri ? "Take a picture now" : "Take a selfie"}
          </button>
        )}

        {fotocameraAperta && (
          <span
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--qpsc-space-8)",
            }}
          >
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              width={320}
              videoConstraints={{ facingMode: "user" }}
              style={{ maxWidth: "100%", borderRadius: "var(--qpsc-radius)" }}
            />
            <span style={{ display: "flex", gap: "var(--qpsc-space-12)" }}>
              <button
                type="button"
                className="qpsc-tool-btn"
                onClick={scattaSelfie}
              >
                Capture
              </button>
              <button
                type="button"
                className="qpsc-ghost-btn"
                onClick={() => setFotocameraAperta(false)}
              >
                Cancel
              </button>
            </span>
          </span>
        )}

        {/* ⚠️ IL DOCUMENTO NON BLOCCA (D-E rivista), e la nota lo dice. Non è
            nel computo del server (`calcolaEnrolled` lascia fuori tutti i
            file) e non è fra gli `OBBLIGATORI_*`: non sbarra il Save e non
            tiene spento il timbro. Renderlo bloccante toglierebbe l'app — e
            con essa la chat con la segreteria — proprio a chi il documento
            deve ancora procurarselo, che è il canale da cui il club glielo
            chiederà. */}
        <Campo
          etichetta="ID document"
          nota={
            (eMinore
              ? "Birth certificate, passport or similar. "
              : "Driver's licence, passport or similar. ") +
            "It is not required to save this form, but the club will need it " +
            "for verification."
          }
        >
          <input
            key={`id-${chiaveFile}`}
            className="qpsc-input"
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            onChange={scegliDocumento(setDocumentoId)}
          />
        </Campo>

        {documento.idDocument && documento.idDocument.url && (
          <button
            type="button"
            className="qpsc-ghost-btn"
            onClick={() => scarica("id-document", "id-document")}
          >
            Open the ID document already on file
          </button>
        )}

        {/* Il terzo allegato è dell'adulto: il coach ci mette il certificato
            WWC, gli altri ciò che il club ha chiesto. Sulla scheda di un
            minore l'incarico chiede foto e documento d'identità, e basta. */}
        {!eMinore && (
          <>
            <Campo
              etichetta="Other document"
              nota={
                figura === "COACH"
                  ? "Coaches: please attach your Working with Children Check certificate here."
                  : "Anything else the club has asked you for."
              }
            >
              <input
                key={`altro-${chiaveFile}`}
                className="qpsc-input"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={scegliDocumento(setAltroDocumento)}
              />
            </Campo>

            {documento.otherDocument && documento.otherDocument.url && (
              <button
                type="button"
                className="qpsc-ghost-btn"
                onClick={() => scarica("other-document", "other-document")}
              >
                Open the other document already on file
              </button>
            )}
          </>
        )}
      </Sezione>

      {/* I TERMINI, RIACCETTATI PER LA STAGIONE (D-A).
          La formula legale è quella dei moduli d'iscrizione, presa dalla sua
          sede unica (`LinkTermini`, enrolShared): il testo dei quattro
          documenti si raggiunge dai link e non viene ripresentato in primo
          piano. Una spunta per SCHEDA, come nel percorso famiglia
          dell'iscrizione: il genitore firma per sé e per ciascun figlio. */}
      <Sezione titolo="Terms & Policies">
        <p className="qpsc-note">
          Every season the club asks you to check these details and to accept
          the terms again. Please tick the box below: it is required before
          you can save.
        </p>

        <label className="qpsc-check">
          <input
            className="qpsc-check__input"
            type="checkbox"
            checked={terminiAccettati}
            onChange={(e) => setTerminiAccettati(e.target.checked)}
          />
          <span className="qpsc-check__box" aria-hidden="true" />
          <LinkTermini />
        </label>

        {perAltri && (
          <p className="qpsc-note">
            As parent or guardian, I accept the above on behalf of this child.
          </p>
        )}
      </Sezione>

      {/* «Save» NON è sbarrato dai campi obbligatori vuoti: il salvataggio
          parziale è la regola di questo modulo. È sbarrato da un campo
          scritto male, mentre un invio è in volo, e dalla casella dei
          termini.
          ⚠️ Quest'ultima è l'unico allargamento della regola, ed è voluto: il
          Save non è più solo un salvataggio parziale, è anche l'atto che
          conferma la stagione (`confirm-season`, in `salva`), e quell'atto
          senza la riaccettazione non si può compiere. */}
      <button
        type="submit"
        className="qpsc-tool-btn"
        disabled={
          salvataggioInCorso || problemiDiForma.length > 0 || !terminiAccettati
        }
      >
        {salvataggioInCorso ? "Saving…" : "Save"}
      </button>

      {/* F2: l'esito anche qui, accanto al bottone che l'ha prodotto. */}
      {ramoFamiglia && esiti(false)}

      {!terminiAccettati && (
        <p className="qpsc-note">
          Please accept the Terms &amp; Policies above before saving.
        </p>
      )}

      {problemiDiForma.length > 0 && (
        <p className="qpsc-note" style={{ color: "var(--qpsc-red-text)" }}>
          {problemiDiForma.join(" ")}
        </p>
      )}
    </form>
  );
}

// ======================================================================
// UNA RIGA DELL'ELENCO DI FAMIGLIA
// ======================================================================
// Lista APRIBILE (Q2): la riga mostra nome e timbro, e si apre sulla scheda.
// Una pagina unica con la scheda del genitore e quelle di tre figli sarebbe
// lunghissima e nessuno saprebbe dove si trova.
function RigaFamiglia({
  titolo,
  sottotitolo,
  stato,
  aperta,
  apribile = true,
  onScambia,
  children,
}) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--qpsc-space-12)",
        padding: "var(--qpsc-space-16)",
        borderRadius: "var(--qpsc-radius)",
        background: "var(--qpsc-surface)",
        boxShadow: "var(--qpsc-shadow)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "var(--qpsc-space-8)",
        }}
      >
        <span
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--qpsc-space-4)",
            minWidth: 0,
          }}
        >
          <span
            style={{
              fontSize: "var(--qpsc-fs-l)",
              fontWeight: 700,
              color: "var(--qpsc-gold-light)",
              overflowWrap: "break-word",
            }}
          >
            {titolo}
          </span>
          {sottotitolo ? (
            <span className="qpsc-note">{sottotitolo}</span>
          ) : null}
        </span>

        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--qpsc-space-12)",
          }}
        >
          <Timbro stato={stato} />
          {/* Niente comando dove non c'è nulla da aprire: una riga in attesa
              o in errore non ha scheda, e un bottone che non fa nulla è
              peggio di un bottone assente. */}
          {apribile && (
            <button
              type="button"
              className="qpsc-ghost-btn"
              onClick={onScambia}
              aria-expanded={aperta}
            >
              {aperta ? "Close" : "Open"}
            </button>
          )}
        </span>
      </div>

      {aperta ? children : null}
    </div>
  );
}

// ======================================================================
// LA PAGINA
// ======================================================================

const HomeIncomplete = () => {
  const { user, refreshUserFromBackend } = useAuth() || {};

  // La figura, col METRO DEL SERVER. Vedi `figuraDelDocumento`.
  const figura = figuraDelDocumento(user);

  // ⚠️ CHIAVE STABILE, non l'oggetto `user`. `refreshUserFromBackend`
  // restituisce un oggetto NUOVO a ogni chiamata: un effetto che dipendesse da
  // `user` ricaricherebbe i figli a ogni salvataggio, e la rilettura dentro il
  // salvataggio ne innescherebbe un'altra. Si dipende dagli ID, in una stringa.
  const idsFigli = useMemo(() => idsDeiFigli(user), [user]);
  const chiaveFigli = idsFigli.join("|");

  // L'elenco dei figli, uno stato per riga:
  //   { id, stato: "caricamento" | "ok" | "errore", documento, negato }
  const [figli, setFigli] = useState([]);
  // Quale scheda è aperta: "io" oppure l'id di un figlio. Una sola alla volta.
  const [aperta, setAperta] = useState(null);
  const aperturaDecisa = useRef(false);
  // Si incrementa per rifare la lettura dei figli («Try again»).
  const [giroDiLettura, setGiroDiLettura] = useState(0);

  // LETTURA DELLE SCHEDE DEI FIGLI.
  // ⚠️ Una GET per figlio, e non c'è alternativa: la lista completa
  // (GET /users) è riservata agli amministratori. Il permesso è il ramo TUTORE
  // di GET /users/:id (eProprietarioOTutore, server.js), che guarda
  // i `guardians` del FIGLIO: una voce di `linkedProfiles` senza il legame di
  // ritorno risponde 403, e la riga lo dice invece di sparire.
  useEffect(() => {
    if (figura !== "PARENT") return undefined;

    const ids = chiaveFigli ? chiaveFigli.split("|") : [];

    if (ids.length === 0) {
      setFigli([]);
      return undefined;
    }

    let annullato = false;

    setFigli(
      ids.map((id) => ({ id, stato: "caricamento", documento: null, negato: false }))
    );

    Promise.all(
      ids.map(async (id) => {
        try {
          const documento = await api.get(`/users/${encodeURIComponent(id)}`);
          return { id, stato: "ok", documento, negato: false };
        } catch (err) {
          // 403 (nessun legame di ritorno) e 404 (documento sparito) sono lo
          // stesso guaio per chi guarda: la scheda non è raggiungibile, e la
          // ripara la segreteria (RL-7d). Gli altri sono guasti di rete, e
          // conviene riprovare.
          const negato = err && (err.status === 403 || err.status === 404);
          return { id, stato: "errore", documento: null, negato };
        }
      })
    ).then((esiti) => {
      if (!annullato) setFigli(esiti);
    });

    return () => {
      annullato = true;
    };
  }, [figura, chiaveFigli, giroDiLettura]);

  // Si apre da sola la prima scheda da completare, una volta sola: chi arriva
  // qui deve trovare qualcosa da fare, non una lista di caselle chiuse.
  useEffect(() => {
    if (figura !== "PARENT") return;
    if (aperturaDecisa.current) return;
    // ⚠️ Si aspetta che l'elenco esista PRIMA di decidere. I due effetti
    // girano dopo lo stesso render, in ordine di dichiarazione: al primo giro
    // `figli` è ancora l'array vuoto dello stato iniziale, non «nessun
    // figlio», e senza questa riga la scelta cadrebbe sempre sulla propria
    // scheda.
    if (idsFigli.length > 0 && figli.length === 0) return;
    if (figli.some((f) => f.stato === "caricamento")) return;

    aperturaDecisa.current = true;

    if (!schedaCompletaPerCortesia(user, "PARENT")) {
      setAperta("io");
      return;
    }

    const daFare = figli.find(
      (f) => f.stato === "ok" && !timbroAcceso(f.documento)
    );

    setAperta(daFare ? daFare.id : "io");
  }, [figura, figli, idsFigli, user]);

  const scambia = (chiave) =>
    setAperta((precedente) => (precedente === chiave ? null : chiave));

  // ⚠️ LE BOZZE — il digitato-e-non-ancora-salvato, e perché sta QUI e non
  // dentro la scheda.
  //
  // La riga di famiglia rende la scheda solo quando è aperta
  // ({aperta ? children : null}): chiudere una scheda la SMONTA. Finché il
  // modulo era uno stato interno a SchedaProfilo, smontarla lo buttava via —
  // un genitore che compilava la scheda di un figlio e apriva quella di un
  // altro perdeva tutto senza un avviso, e riaprendo la prima ritrovava i
  // valori del server. Tenendo il modulo nel padre, indicizzato per la stessa
  // chiave che usa `aperta` ("io" per sé, l'id per ogni figlio), la scheda può
  // smontarsi quanto vuole: il testo resta.
  //
  // ⚠️ Non si è invece montata la scheda chiusa (nascosta, o con display:none):
  // sarebbe stata la scorciatoia, e sarebbe costata una raffica di richieste.
  // Montare una scheda fa partire le fetch autenticate delle foto già in
  // archivio, che oggi partono SOLO quando quella scheda viene aperta: con
  // tre figli si scaricherebbero tre foto che nessuno ha chiesto di vedere.
  // La riga di RigaFamiglia resta com'è.
  //
  // ⚠️ La bozza dura quanto la pagina, e nulla di più: ricaricare il browser o
  // chiudere l'app riparte dal documento del server. Niente localStorage,
  // sessionStorage, cookie o IndexedDB — sono dati di minori e non devono
  // restare scritti sul telefono a sessione chiusa.
  const [bozze, setBozze] = useState({});

  const bozzaDi = (chiave, documento) =>
    bozze[chiave] || moduloDalDocumento(documento);

  const cambiaBozza = (chiave, documento) => (aggiornamento) =>
    setBozze((precedenti) => {
      const corrente = precedenti[chiave] || moduloDalDocumento(documento);
      const nuova =
        typeof aggiornamento === "function" ? aggiornamento(corrente) : aggiornamento;
      return { ...precedenti, [chiave]: nuova };
    });

  // La rilettura di SÉ STESSI: è `refreshUserFromBackend`, cioè il passaggio
  // che rimette in AuthContext il documento nuovo e, se il timbro si è acceso,
  // fa ridisegnare il cancello.
  const rileggiSeStesso = async () =>
    refreshUserFromBackend ? refreshUserFromBackend() : null;

  // La rilettura di un FIGLIO: il suo documento, per aggiornare la riga; e poi
  // il proprio, perché la cascata di famiglia può aver acceso il timbro del
  // genitore ed è quello che apre l'app. In quest'ordine: prima si aggiorna
  // ciò che si vede, poi si chiede al cancello di decidere.
  const rileggiFiglio = (id) => async () => {
    const documento = await api.get(`/users/${encodeURIComponent(id)}`);

    setFigli((precedenti) =>
      precedenti.map((f) =>
        f.id === id ? { ...f, stato: "ok", documento, negato: false } : f
      )
    );

    if (refreshUserFromBackend) await refreshUserFromBackend();

    return documento;
  };

  // Il documento non è ancora arrivato: il cancello non ci manda mai qui senza
  // utente, ma il componente non deve dipendere da questo.
  // ⚠️ Le tre rese qui sotto accendono `showLogout` (cantiere LOGOUT-1, vedi
  // UserBar.jsx): questa finestra è un vicolo cieco — il cancello ci tiene
  // chi deve completare, e da qui More non si raggiunge.
  if (!user) {
    return (
      <HomeShell showLogout>
        <h1 className="qpsc-page-title">Complete your details</h1>
        <p className="qpsc-body-text">Loading your record…</p>
      </HomeShell>
    );
  }

  // ────────────────────────────────────────────────────────────────────
  // PERCORSO DI RL-7a — chi non è un genitore compila sé stesso, e basta.
  // Nessun elenco, nessuna scheda altrui: la pagina è quella di prima.
  // ────────────────────────────────────────────────────────────────────
  if (figura !== "PARENT") {
    return (
      <HomeShell showLogout>
        <h1 className="qpsc-page-title">Complete your details</h1>

        <p className="qpsc-body-text">
          Your account is active. Before you can use the app, the club needs
          the details below. Fields marked with * are required; everything else
          is optional and does not hold up your membership.
        </p>

        <p className="qpsc-body-text qpsc-body-text--muted">
          You can save at any time and come back later: whatever you have saved
          will still be here.
        </p>

        <SchedaProfilo
          documento={user}
          figura={figura}
          rileggi={rileggiSeStesso}
          form={bozzaDi("io", user)}
          setForm={cambiaBozza("io", user)}
        />
      </HomeShell>
    );
  }

  // ────────────────────────────────────────────────────────────────────
  // PERCORSO DI RL-7b — LA FAMIGLIA.
  // ────────────────────────────────────────────────────────────────────
  const inCaricamento = figli.some((f) => f.stato === "caricamento");
  const qualcheErrore = figli.some((f) => f.stato === "errore");

  // ⚠️ Le tre condizioni dell'avviso in coda, e perché stanno insieme.
  // Il timbro del genitore è spento (siamo qui), la sua scheda sembra a posto
  // e ogni figlio LETTO risulta completo: allora ciò che manca non si vede da
  // questa pagina. Il caso vero è il legame a una sola faccia — un figlio che
  // elenca il genitore fra i propri `guardians` senza comparire nei suoi
  // `linkedProfiles`: il server lo conta nella cascata, l'elenco non lo mostra,
  // e nessuno capirebbe perché l'app non si apre. Non lo si ripara qui
  // (RL-7d): lo si manda in segreteria.
  const propriaScheda = schedaCompletaPerCortesia(user, "PARENT");
  const figliTuttiCompleti =
    !inCaricamento &&
    !qualcheErrore &&
    figli.every((f) => timbroAcceso(f.documento));
  // ⚠️ LA QUARTA CONDIZIONE (RENEW-COMPLETAMENTO). Chi è qui per la conferma
  // della stagione vede tutte le schede complete ed è normale che sia così:
  // non gli manca un dato, gli manca un gesto. L'avviso qui sotto direbbe il
  // falso e manderebbe in segreteria chi deve solo spuntare una casella e
  // premere Save.
  const nullaDaVedere =
    propriaScheda &&
    figliTuttiCompleti &&
    user.deveCompletareStagione !== true;

  return (
    <HomeShell showLogout>
      <h1 className="qpsc-page-title">Complete your details</h1>

      <p className="qpsc-body-text">
        Your account is active. Before you can use the app, the club needs the
        details of everyone in your family: yours and one record for each of
        your children. Fields marked with * are required; everything else is
        optional and does not hold up your membership.
      </p>

      <p className="qpsc-body-text qpsc-body-text--muted">
        Open a record, fill in what you can and press Save. You can save at any
        time and come back later: whatever you have saved will still be here.
      </p>

      {/* La regola di famiglia (decisione D2), detta prima dell'elenco: senza
          questa riga un genitore con la propria scheda in ordine vedrebbe il
          timbro restare spento senza alcuna spiegazione. */}
      <p className="qpsc-body-text qpsc-body-text--muted">
        The app opens when every record below is complete — yours and your
        children's. The club's system has the final word on that: the marks
        beside each name are there to guide you.
      </p>

      {/* UX-COMPLETAMENTO (F5): spunta e Save sono PER SCHEDA, e il passo
          dopo è l'enrollment della stagione nuova (confermato dal
          committente). A schermo «enrol», mai «register» (CLAUDE.md §3.7). */}
      <p className="qpsc-body-text">
        Each record is saved on its own — tick the box and press Save on every
        one. Once all are complete, the app opens and you can enrol for next
        season.
      </p>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--qpsc-space-16)",
        }}
      >
        {/* LA PROPRIA SCHEDA, prima di tutte.
            ⚠️ Il suo segno NON viene da `enrolled`: quel campo, sul genitore,
            è il verdetto sulla FAMIGLIA (enrolledConFamiglia) e qui è spento
            per definizione. Vedi `schedaCompletaPerCortesia`. */}
        <RigaFamiglia
          titolo={nomeDelDocumento(user) || "Your record"}
          sottotitolo="You"
          stato={propriaScheda ? "completo" : "incompleto"}
          aperta={aperta === "io"}
          onScambia={() => scambia("io")}
        >
          <SchedaProfilo
            documento={user}
            figura="PARENT"
            rileggi={rileggiSeStesso}
            form={bozzaDi("io", user)}
            setForm={cambiaBozza("io", user)}
          />
        </RigaFamiglia>

        {figli.map((figlio) => {
          if (figlio.stato === "caricamento") {
            return (
              <RigaFamiglia
                key={figlio.id}
                titolo="Loading…"
                stato="ignoto"
                aperta={false}
                apribile={false}
                onScambia={() => {}}
              />
            );
          }

          if (figlio.stato === "errore") {
            return (
              <RigaFamiglia
                key={figlio.id}
                titolo="A child's record"
                sottotitolo={
                  figlio.negato
                    ? "The club has not linked this record to you, or it no longer exists. Please contact the club."
                    : "This record could not be loaded. Please check your connection and try again."
                }
                stato="ignoto"
                aperta={false}
                apribile={false}
                onScambia={() => {}}
              />
            );
          }

          const documento = figlio.documento;
          const figuraFiglio = figuraDelDocumento(documento);

          return (
            <RigaFamiglia
              key={figlio.id}
              titolo={nomeDelDocumento(documento) || "A child's record"}
              sottotitolo={
                figuraFiglio === "MINOR" ? "Junior member" : "Linked record"
              }
              stato={timbroAcceso(documento) ? "completo" : "incompleto"}
              aperta={aperta === figlio.id}
              onScambia={() => scambia(figlio.id)}
            >
              {/* ⚠️ La figura si legge dal DOCUMENTO del figlio, non si
                  presume: un profilo collegato che abbia compiuto diciotto
                  anni non è più `isMinor`, e il server smette di chiedergli
                  scuola e lingua di casa. Il modulo lo segue. */}
              <SchedaProfilo
                documento={documento}
                figura={figuraFiglio}
                genitore={user}
                rileggi={rileggiFiglio(figlio.id)}
                form={bozzaDi(figlio.id, documento)}
                setForm={cambiaBozza(figlio.id, documento)}
              />
            </RigaFamiglia>
          );
        })}
      </div>

      {qualcheErrore && (
        <button
          type="button"
          className="qpsc-ghost-btn"
          onClick={() => setGiroDiLettura((n) => n + 1)}
        >
          Try loading the records again
        </button>
      )}

      {/* Nessun figlio collegato: non lo si aggiusta da qui (RL-7d), lo si
          dice. Un genitore che ha iscritto dei figli e non li vede deve poter
          capire subito che il guaio è nel legame, non nel modulo. */}
      {idsFigli.length === 0 && (
        <p className="qpsc-note">
          The club has no children linked to your account. If that is not
          right, please contact the club.
        </p>
      )}

      {nullaDaVedere && (
        <p className="qpsc-note" style={{ color: "var(--qpsc-red-text)" }}>
          Everything on this page looks complete, but the club's system still
          reports something missing. Please save each record once, and if
          nothing changes contact the club.
        </p>
      )}
    </HomeShell>
  );
};

export default HomeIncomplete;
